import React, { useState, useEffect } from "react";
import { Dialog, Box, List, ListItem, Checkbox, Typography, IconButton, Button } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useTranslation } from "react-i18next";

interface Props {
    open: boolean;
    onClose: () => void;
    onSave: (graphs: string[]) => void;
    available: { key: string; label: string }[];
    selected: string[];
}

const GraphBuilder: React.FC<Props> = ({ open, onClose, onSave, available, selected }) => {
    const { t } = useTranslation();
    const [graphs, setGraphs] = useState<string[]>(selected);

    useEffect(() => {
        setGraphs(selected);
    }, [selected, open]);

    const toggle = (key: string) => {
        setGraphs(prev => prev.includes(key) ? prev.filter(g => g !== key) : [...prev, key]);
    };

    const move = (index: number, offset: number) => {
        const newIndex = index + offset;
        if (newIndex < 0 || newIndex >= graphs.length) return;
        const updated = [...graphs];
        const [item] = updated.splice(index, 1);
        updated.splice(newIndex, 0, item);
        setGraphs(updated);
    };

    const save = () => {
        onSave(graphs);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <Box p={3}>
                <Typography variant="h5" gutterBottom>{t('graphBuilder.title')}</Typography>
                <List>
                    {available.map(opt => {
                        const isSelected = graphs.includes(opt.key);
                        return (
                            <ListItem key={opt.key}>
                                <Checkbox checked={isSelected} onChange={() => toggle(opt.key)} />
                                <Typography sx={{ flexGrow: 1 }}>{opt.label}</Typography>
                            </ListItem>
                        );
                    })}
                    {/* Selected and reorderable list shown in order */}
                    {graphs.map((key, idx) => {
                        const opt = available.find(o => o.key === key);
                        if (!opt) return null;
                        return (
                            <ListItem key={`${key}-reorder`}>
                                <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                                    <Typography sx={{ flexGrow: 1 }}>{opt.label}</Typography>
                                    <IconButton onClick={() => move(idx, -1)} size="small" disabled={idx === 0}>
                                        <ArrowUpwardIcon />
                                    </IconButton>
                                    <IconButton onClick={() => move(idx, 1)} size="small" disabled={idx === graphs.length - 1}>
                                        <ArrowDownwardIcon />
                                    </IconButton>
                                </Box>
                            </ListItem>
                        );
                    })}
                </List>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button variant="contained" onClick={save}>{t('graphBuilder.save')}</Button>
                </Box>
            </Box>
        </Dialog>
    );
};

export default GraphBuilder;
